import React, { useEffect, useMemo, useState } from "react";
import { Table, Pagination, Spin, Empty } from "antd";
import { useIsMobile } from "./useMediaQuery";
import "./ResponsiveTable.css";

/*
 * Drop-in replacement for antd <Table>. Desktop/tablet: the same table, horizontally scrollable.
 * Phones: one card per row — the title column on top, other columns as label/value pairs, the
 * actions column in the card footer.
 *
 * Optional per-column hints (ignored by the desktop table):
 *   mobile: "title"    card heading (default: first visible column)
 *   mobile: "subtitle" small line under the heading
 *   mobile: "badge"    top-end of the card, next to the heading (default: a status/priority
 *                      column)
 *   mobile: "actions"  card footer (default: a column keyed/titled action(s)/operation); a lone
 *                      row menu (DropdownComp) moves to the card's top-end corner instead
 *   mobile: "full"     field spanning both columns of the field grid
 *   mobile: "hidden"   left out of the card
 *   mobileLabel        label to use instead of the column title
 *
 * `mobileAvatar`: true | false | record => node. By default a card whose heading is a person or
 * company name (title column keyed like *name/client/employee/...) gets an initials avatar.
 *
 * mobileLayout="table" keeps the scrollable table on phones (wide numeric grids, editable
 * cells). Tables with rowSelection or expandable rows do that automatically.
 */

const ACTION_KEY = /^(action|actions|operation|operations|edit|options)$/i;
const BADGE_KEY = /(^|\.)(status|priority|state)$/i;
const NAME_KEY = /(name|client|employee|customer|user|vendor|contact|member|person|company)/i;
const AVATAR_TINTS = 6;

// Mirrors antd: with no dataIndex (or an empty path) a column's value is the whole record, and
// many columns rely on that (`render: record => record.date`).
const valueAt = (record, dataIndex) => {
  if (dataIndex === undefined || dataIndex === null) return record;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  if (path.length === 0) return record;
  return path.reduce((obj, key) => (obj == null ? undefined : obj[key]), record);
};

const flattenColumns = columns =>
  (columns || []).flatMap(col => (col?.children?.length ? flattenColumns(col.children) : [col]));

const titleText = col => {
  if (col.mobileLabel !== undefined) return col.mobileLabel;
  return typeof col.title === "function" ? col.title({}) : col.title;
};

const isActionColumn = col =>
  col.mobile === "actions" ||
  (col.mobile === undefined &&
    (ACTION_KEY.test(String(col.key ?? "")) ||
      ACTION_KEY.test(String(col.dataIndex ?? "")) ||
      (typeof col.title === "string" && ACTION_KEY.test(col.title.trim()))));

const colId = col =>
  String(
    col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join(".") : (col.dataIndex ?? ""))
  );

// Status-like columns become the card's badge, also when an older page hinted them as subtitle.
const isBadgeColumn = col =>
  col.mobile === "badge" ||
  ((col.mobile === undefined || col.mobile === "subtitle") && BADGE_KEY.test(colId(col)));

// Plain text of a rendered cell (e.g. <>{first} {last}</>), for the initials avatar.
const nodeText = node => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (React.isValidElement(node)) return nodeText(node.props.children);
  return "";
};

const initialsOf = text =>
  String(text)
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase();

const tintOf = text => {
  let h = 0;
  for (const ch of String(text)) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return Math.abs(h) % AVATAR_TINTS;
};

// Long plain values (descriptions, emails, addresses) get the full card width.
const isWideValue = v => typeof v === "string" && (v.length > 22 || v.includes("@"));

const renderCell = (col, record, index) => {
  const value = valueAt(record, col.dataIndex);
  // Without a render, only primitives are displayable (never the record object itself).
  if (!col.render) return value !== null && typeof value === "object" ? undefined : value;
  const out = col.render(value, record, index);
  // antd lets render return { children, props } for colSpan tricks.
  return out && typeof out === "object" && "children" in out && "props" in out ? out.children : out;
};

const isEmptyNode = node => node === undefined || node === null || node === "" || node === false;

const getRowKey = (rowKey, record, index) => {
  if (typeof rowKey === "function") return rowKey(record, index);
  if (typeof rowKey === "string") return record?.[rowKey] ?? index;
  return record?.key ?? record?._id ?? index;
};

const MobileCards = ({
  columns,
  dataSource = [],
  rowKey,
  loading,
  pagination,
  onChange,
  onRow,
  locale,
  title,
  footer,
  className,
  mobileAvatar
}) => {
  const cols = useMemo(
    () => flattenColumns(columns).filter(c => c && !c.hidden && c.mobile !== "hidden"),
    [columns]
  );
  const actionCols = cols.filter(isActionColumn);
  const contentCols = cols.filter(c => !isActionColumn(c));
  const titleCol =
    contentCols.find(c => c.mobile === "title") ||
    contentCols.find(c => !isBadgeColumn(c)) ||
    contentCols[0];
  const subtitleCols = contentCols.filter(
    c => c !== titleCol && c.mobile === "subtitle" && !isBadgeColumn(c)
  );
  const badgeCols = contentCols.filter(c => c !== titleCol && isBadgeColumn(c));
  const fieldCols = contentCols.filter(
    c => c !== titleCol && c.mobile !== "subtitle" && !badgeCols.includes(c)
  );
  const autoAvatar = mobileAvatar === undefined && titleCol && NAME_KEY.test(colId(titleCol));

  const avatarFor = (record, heading) => {
    if (typeof mobileAvatar === "function") return mobileAvatar(record);
    if (mobileAvatar === false || (!mobileAvatar && !autoAvatar)) return null;
    const text = nodeText(heading).trim();
    if (!text || text.length > 60) return null;
    const initials = initialsOf(text);
    if (!initials) return null;
    return (
      <span className={`rt-card__avatar rt-card__avatar--${tintOf(text)}`} aria-hidden="true">
        {initials}
      </span>
    );
  };

  // Pagination: controlled when the caller passes `current`, otherwise sliced locally like antd.
  const paginationOn = pagination !== false;
  const pg = paginationOn && typeof pagination === "object" ? pagination : {};
  const controlled = pg.current !== undefined;
  const [localPage, setLocalPage] = useState(pg.defaultCurrent || 1);
  const [localSize, setLocalSize] = useState(pg.defaultPageSize || pg.pageSize || 10);
  const current = controlled ? pg.current : localPage;
  const pageSize = pg.pageSize ?? localSize;
  const total = pg.total ?? dataSource.length;
  const serverPaged = controlled && pg.total !== undefined && pg.total > dataSource.length;

  useEffect(() => {
    if (!controlled && (localPage - 1) * pageSize >= dataSource.length && localPage > 1) {
      setLocalPage(1);
    }
  }, [dataSource.length, controlled, localPage, pageSize]);

  const rows =
    paginationOn && !serverPaged
      ? dataSource.slice((current - 1) * pageSize, current * pageSize)
      : dataSource;

  const handlePage = (page, size) => {
    if (!controlled) {
      setLocalPage(page);
      setLocalSize(size);
    }
    pg.onChange?.(page, size);
    onChange?.({ ...pg, current: page, pageSize: size }, {}, {}, { action: "paginate" });
  };

  const empty = locale?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <div className={`rt-cards ${className || ""}`}>
      {title && <div className="rt-cards__title">{title(rows)}</div>}
      <Spin spinning={Boolean(typeof loading === "object" ? loading.spinning : loading)}>
        {rows.length === 0 ? (
          <div className="rt-cards__empty">{empty}</div>
        ) : (
          <ul className="rt-cards__list">
            {rows.map((record, i) => {
              const index = serverPaged ? i : (current - 1) * pageSize + i;
              const rowProps = onRow?.(record, index) || {};
              const clickable = Boolean(rowProps.onClick);
              const heading = titleCol ? renderCell(titleCol, record, index) : null;
              return (
                <li
                  key={getRowKey(rowKey, record, index)}
                  className={`rt-card${clickable ? " rt-card--clickable" : ""}`}
                  onClick={rowProps.onClick}
                  onKeyDown={
                    clickable
                      ? e => {
                          if (
                            e.target === e.currentTarget &&
                            (e.key === "Enter" || e.key === " ")
                          ) {
                            e.preventDefault();
                            rowProps.onClick(e);
                          }
                        }
                      : undefined
                  }
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? "button" : undefined}
                >
                  <div className="rt-card__head">
                    {avatarFor(record, heading)}
                    <div className="rt-card__heading">
                      {!isEmptyNode(heading) && <div className="rt-card__title">{heading}</div>}
                      {subtitleCols.map(col => {
                        const v = renderCell(col, record, index);
                        return isEmptyNode(v) ? null : (
                          <div className="rt-card__subtitle" key={colId(col)}>
                            {v}
                          </div>
                        );
                      })}
                    </div>
                    {badgeCols.length > 0 && (
                      <div className="rt-card__badges">
                        {badgeCols.map(col => {
                          const v = renderCell(col, record, index);
                          return isEmptyNode(v) ? null : (
                            <React.Fragment key={colId(col)}>{v}</React.Fragment>
                          );
                        })}
                      </div>
                    )}
                    {clickable && actionCols.length === 0 && (
                      <span className="rt-card__chevron" aria-hidden="true" />
                    )}
                  </div>
                  {fieldCols.length > 0 && (
                    <dl className="rt-card__fields">
                      {fieldCols.map(col => {
                        const v = renderCell(col, record, index);
                        const wide = col.mobile === "full" || isWideValue(v);
                        return (
                          <div
                            className={`rt-card__field${
                              col.align === "right" || col.align === "end"
                                ? " rt-card__field--end"
                                : ""
                            }${wide ? " rt-card__field--full" : ""}`}
                            key={colId(col)}
                          >
                            <dt>{titleText(col)}</dt>
                            <dd dir="auto">{isEmptyNode(v) ? "—" : v}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  )}
                  {actionCols.length > 0 && (
                    // Actions must not also trigger the card's own onClick.
                    <div className="rt-card__actions" onClick={e => e.stopPropagation()}>
                      {actionCols.map(col => (
                        <React.Fragment key={colId(col)}>
                          {renderCell(col, record, index)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Spin>
      {paginationOn && total > pageSize && (
        <Pagination
          className="rt-cards__pagination"
          simple
          current={current}
          pageSize={pageSize}
          total={total}
          onChange={handlePage}
        />
      )}
      {footer && <div className="rt-cards__footer">{footer(rows)}</div>}
    </div>
  );
};

const ResponsiveTable = ({ mobileLayout = "cards", mobileAvatar, scroll, ...props }) => {
  const isMobile = useIsMobile();
  const needsTable = props.rowSelection || props.expandable || mobileLayout === "table";

  if (isMobile && !needsTable) return <MobileCards mobileAvatar={mobileAvatar} {...props} />;

  return <Table scroll={{ x: "max-content", ...scroll }} {...props} />;
};

export default ResponsiveTable;
